import FolderOpen from "@mui/icons-material/FolderOpen";
import {
  Button,
  Grid,
  IconButton,
  LinearProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import NavigationLock from "./NavigationLock";

import { PageTitleBar } from "common/components";
import { useTranslation } from "common/locales";
import RelevantFilesList from "features/programmesActions/RelevantFilesList";
import {
  addFilledOut,
  fetchAllProgrammeActions,
  ProgrammeActions,
  selectProgrammeActionsById,
  useAppDispatch,
  useAppSelector,
} from "model";
import {
  setQuestionnaireFinished,
  setQuestionnaireStarted,
} from "model/questionnaireStartedSlice";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import styled, { css } from "styled-components";
import ActionList from "./ActionList";
import CenteredTextBox from "./CenteredTextBox";
import StepCard from "./FirstStepCard";
import ImageWithCowOverlay from "./ImagewitHCowOverlay";

export default function ProgrammeDetailsDrawer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { programmeId } = useParams<{ programmeId: string }>();
  const REFRESH_INTERVAL = 1 * 1000;
  const programmeActionsQuestionnaireFilledOutIds = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).programmeActionsQuestionnaireFilledOutIds
  );

  const [viewMore, setViewMore] = useState(false);
  const questionnaireStarted = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).questionnaireStarted
  );

  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user.details);
  const history = useHistory();

  const handleBackToFarm = () => {
    history.push(`/file-management-overview`);
  };
  const dispatch = useAppDispatch();

  const programmeActions = useAppSelector((state) =>
    selectProgrammeActionsById(state, programmeId)
  );

  const loading = useAppSelector(
    (state) => state.programmeActions.loading === "pending"
  );
  const hasSelectedActions = !(
    !programmeActions?.selectedActions ||
    Object.keys(programmeActions.selectedActions).length === 0
  );
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleMessages = (content: any) => {
      if (!content) {
        return;
      }
      // Check if submission is completed.
      if (
        typeof content.data === "object" &&
        content.data.action === "submission-completed"
      ) {
        dispatch(addFilledOut({ filledOutProgrammeId: programmeId }));
        return;
      }
    };

    if (window.addEventListener) {
      window.addEventListener("message", handleMessages, true); // Capture
    }
    return () => {
      if (window.removeEventListener) {
        window.removeEventListener("message", handleMessages, true);
      }
    };
  }, [dispatch, programmeId]);

  useEffect(() => {
    const unsetQuestionnaireStarted = history.listen((e) => {
      if (e.pathname !== `/programme-details/${programmeId}`) {
        // Returning false allows navigation, returning a string shows a prompt
        dispatch(setQuestionnaireFinished(true));
      }
    });

    return function cleanup() {
      unsetQuestionnaireStarted();
    };
  }, [dispatch, history, programmeId]);

  useEffect(() => {
    const onBeforeUnload = (ev: Event) => {
      if (questionnaireStarted.questionnaireStarted) {
        ev.preventDefault();
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireStarted.questionnaireStarted]);

  const filledOutButResultNotAvailableYet =
    programmeActionsQuestionnaireFilledOutIds.programmeActionsQuestionnaireFilledOutIds.includes(
      programmeId
    ) && !hasSelectedActions;
  useEffect(() => {
    if (filledOutButResultNotAvailableYet) {
      const reloadProgrammesAndActions = async () => {
        if (!loading) {
          await dispatch(fetchAllProgrammeActions());
        }
        return;
      };
      const refreshInterval = setInterval(
        reloadProgrammesAndActions,
        REFRESH_INTERVAL
      );
      // clear interval when there no fields with invalid data
      if (hasSelectedActions) {
        clearInterval(refreshInterval);
      }
      return () => clearInterval(refreshInterval);
    }
  }, [
    dispatch,
    filledOutButResultNotAvailableYet,
    loading,
    hasSelectedActions,
    REFRESH_INTERVAL,
    programmeActions,
  ]);

  if (loading || filledOutButResultNotAvailableYet) {
    return <LinearProgress />;
  }

  const maxHeight = viewMore ? "auto" : 200;

  const hasQuestionnaireUrl = !!programmeActions?.questionnaireUrl;
  if (hasSelectedActions) {
    return (
      <>
        {isMobile && (
          <PageTitleBar
            title={t("programmes.toolbar.title")}
            showDivider
            children={
              <ProfileIcon
                onClick={handleBackToFarm}
                aria-controls="profile-menu"
                aria-haspopup="true"
              >
                <FolderOpen />
              </ProfileIcon>
            }
          />
        )}
        {ProgrammeNameAndText(
          programmeActions,
          viewMore,
          maxHeight,
          setViewMore,
          user?.name ?? "",
          t("action.plan.submitted.thank.you"),
          t,
          false
        )}

        {<ActionList rawSelectedActions={programmeActions.selectedActions} />}
      </>
    );
  } else if (hasQuestionnaireUrl && questionnaireStarted.questionnaireStarted) {
    return (
      <>
        <NavigationLock
          when={questionnaireStarted.questionnaireStarted}
        ></NavigationLock>

        {!isMobile &&
          ProgrammeNameAndText(
            programmeActions,
            viewMore,
            maxHeight,
            setViewMore,
            user?.name ?? "",
            programmeActions.welcomeText,
            t
          )}

        {!isMobile && <RelevantFilesList />}
        {/* Mobile Dashboard iframe */}
        {isMobile && programmesActionsIframe(programmeActions)}
      </>
    );
  } else if (
    hasQuestionnaireUrl &&
    !questionnaireStarted.questionnaireStarted
  ) {
    return (
      <>
        {isMobile && (
          <PageTitleBar
            title={t("programmes.toolbar.title")}
            showDivider
            children={
              <ProfileIcon
                onClick={handleBackToFarm}
                aria-controls="profile-menu"
                aria-haspopup="true"
              >
                <FolderOpen />
              </ProfileIcon>
            }
          />
        )}
        {ProgrammeNameAndText(
          programmeActions,
          viewMore,
          maxHeight,
          setViewMore,
          user?.name ?? "",
          programmeActions.welcomeText,
          t
        )}
        <StepCard
          stepNumber={1}
          stepTitle="Plan your actions"
          stepDescription="The first step is to plan actions that feel manageable and will make a real impact on your farm."
          buttonText="PLAN YOUR ACTIONS"
          daysLeft={122}
          status="In progress"
          onButtonClick={() => {
            dispatch(setQuestionnaireStarted(true));
          }}
        />
      </>
    );
  } else if (programmeActions) {
    return (
      <>
        {isMobile && (
          <PageTitleBar
            title={t("programmes.toolbar.title")}
            showDivider
            children={
              <ProfileIcon
                onClick={handleBackToFarm}
                aria-controls="profile-menu"
                aria-haspopup="true"
              >
                <FolderOpen />
              </ProfileIcon>
            }
          />
        )}
        {ProgrammeNameAndText(
          programmeActions,
          viewMore,
          maxHeight,
          setViewMore,
          user?.name ?? "",
          programmeActions.welcomeText,
          t
        )}
      </>
    );
  } else {
    return <></>;
  }
}

const ProfileIcon = styled(IconButton)(
  ({ theme }) => `
    background: white;
  :hover {
      background: ${theme.palette.background.default};
    }
    `
);

// Header component replaced with PageTitleBar for consistency
export const programmesActionsIframe = (
  programmesActions: ProgrammeActions
) => {
  return (
    <div
      style={{
        top: 0,
        left: 0,
        margin: 0,
        padding: 0,
        height: "100vh",
        width: "100%",
        position: "absolute",
      }}
    >
      <iframe
        id="JotFormIFrame-252572906128359"
        title="New Customer Registration Form"
        allow="geolocation; microphone; camera; fullscreen; payment"
        src={programmesActions?.questionnaireUrl}
        style={{
          top: 0,
          left: 0,
          margin: 0,
          padding: 0,
          height: "90%",
          width: "100%",
          position: "absolute",
        }}
      ></iframe>
      <script src="https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js"></script>
      <script>
        window.jotformEmbedHandler("iframe[id='JotFormIFrame-252572906128359']",
        "https://form.jotform.com/")
      </script>
    </div>
  );
};

export const ProgrammeNameAndText = (
  programmeActions: ProgrammeActions,
  viewMore: boolean,
  maxHeight: string | number,
  setViewMore: (arg: boolean) => void,
  userName: string,
  text: string,
  t: (a: string) => string,
  readMoreOrLessButton = true
) => {
  return (
    <Grid
      container
      spacing={3}
      sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
    >
      <Grid item xs={12} sm={12} sx={{ textAlign: "center", pb: 8 }}>
        <ImageWithCowOverlay />
      </Grid>

      <Grid item xs={12} sm={12} sx={{ textAlign: "center" }}>
        <Typography
          variant="h4"
          component="h4"
          gutterBottom
          alignContent={"center"}
        >
          {programmeActions.programmeName}
        </Typography>
      </Grid>
      {text && (
        <Grid item xs={12} sm={12}>
          <CenteredTextBox
            text={text.replace("FARMER_NAME", userName)}
            useMaxSpace={viewMore}
            maxHeight={maxHeight}
            lineHeight={20}
          />
        </Grid>
      )}
      {!text && (
        <Grid item xs={12} sm={12}>
          <CenteredTextBox
            text={t("programme.in.developement")}
            useMaxSpace={viewMore}
            maxHeight={maxHeight}
            lineHeight={20}
          />
        </Grid>
      )}
      {text && (
        <Grid
          item
          xs={12}
          sm={12}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignContent: "center",
            alignItems: "center",
          }}
        >
          {readMoreOrLessButton && (
            <Button variant="text" onClick={() => setViewMore(!viewMore)}>
              <MoreLessButton>
                {viewMore ? "Read less" : "Read more"}
              </MoreLessButton>
            </Button>
          )}
        </Grid>
      )}
    </Grid>
  );
};

const MoreLessButton = styled("span")(
  ({ theme }) => css`
    color: var(--Dahlia-700, #2e7831);
    font-feature-settings: "liga" off, "clig" off;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
  `
);
